import type { LocaleConfig } from "../config";

export const LOCALE_PREFERENCE_COOKIE = "locale_pref";

function resolveCookieString(cookieString?: string): string {
  return (
    cookieString ?? (typeof document === "undefined" ? "" : document.cookie)
  );
}

function readCookieValue(cookieString: string, name: string): string | null {
  for (const part of cookieString.split(";")) {
    const raw = part.trim();
    if (!raw.startsWith(`${name}=`)) continue;
    try {
      return decodeURIComponent(raw.slice(name.length + 1));
    } catch {
      return null;
    }
  }
  return null;
}

// resolveLocaleFromPrefix, unlike KNOWN_PREFIXES.includes, also accepts the default prefix.
function isStorablePrefix(config: LocaleConfig, prefix: string): boolean {
  if (prefix === config.PSEUDO_PREFIX) return false;
  return config.resolveLocaleFromPrefix(prefix) !== null;
}

export interface LocalePreferenceConsentOptions {
  /**
   * Required, not defaulted: this package cannot import the consent helper without
   * creating an l10n → marketo → ui → l10n workspace cycle, and a defaultable flag
   * is one a caller can silently forget.
   */
  hasConsent: (cookieString: string) => boolean;
  cookieString?: string;
}

export type ReadLocalePreferenceOptions = LocalePreferenceConsentOptions;

export function readLocalePreference(
  config: LocaleConfig,
  { hasConsent, cookieString }: ReadLocalePreferenceOptions,
): string | null {
  const resolvedCookieString = resolveCookieString(cookieString);
  if (!hasConsent(resolvedCookieString)) return null;

  const value = readCookieValue(resolvedCookieString, LOCALE_PREFERENCE_COOKIE);
  if (!value || !isStorablePrefix(config, value)) return null;
  return value;
}

export type WriteLocalePreferenceOptions = LocalePreferenceConsentOptions;

export function writeLocalePreference(
  config: LocaleConfig,
  prefix: string,
  { hasConsent, cookieString }: WriteLocalePreferenceOptions,
): boolean {
  if (!isStorablePrefix(config, prefix)) return false;
  if (!hasConsent(resolveCookieString(cookieString))) return false;

  const maxAgeSeconds = 60 * 60 * 24 * 365;
  document.cookie = `${LOCALE_PREFERENCE_COOKIE}=${encodeURIComponent(prefix)}; max-age=${maxAgeSeconds}; path=/; SameSite=Lax; Secure`;
  return true;
}

/**
 * Expires the cookie. Deliberately unconditional: withdrawing consent must
 * remove an already-stored preference, so gating this on consent would make it
 * a no-op in exactly the case it exists for. Safe when nothing is stored.
 */
export function clearLocalePreference(): void {
  if (typeof document === "undefined") return;
  document.cookie = `${LOCALE_PREFERENCE_COOKIE}=; max-age=0; path=/; SameSite=Lax; Secure`;
}
